import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PAGES } from "@/lib/pages";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "", role: "operator", allowed_pages: null };

function parseCSV(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = (k) => header.indexOf(k);
  const iName = idx("name"), iEmail = idx("email"), iPw = idx("password"),
        iRole = idx("role"), iPages = idx("allowed_pages");
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim());
    if (!cols[iName] || !cols[iEmail]) continue;
    const pages = iPages >= 0 && cols[iPages]
      ? cols[iPages].split("|").map((s) => s.trim()).filter(Boolean)
      : null;
    rows.push({
      name: cols[iName],
      email: cols[iEmail],
      password: cols[iPw] || "changeme",
      role: cols[iRole] || "operator",
      allowed_pages: pages,
    });
  }
  return rows;
}

export default function UsersPage() {
  const [list, setList] = useState([]);
  const [f, setF] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // user id
  const [restrict, setRestrict] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileInput = useRef(null);

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length === 0) {
      toast.error("No valid rows found in CSV");
      return;
    }
    try {
      const { data } = await api.post("/users/bulk", rows);
      setBulkResult(data);
      toast.success(`Imported ${data.created} of ${data.total} users`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "name,email,password,role,allowed_pages",
      "Ravi Kumar,ravi@slkc.com,pass1234,operator,cutting|dispatch",
      "Meena S,meena@slkc.com,pass1234,storekeeper,stock|dispatch|accessories",
      "Priya P,priya@slkc.com,pass1234,operator,",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "slkc-users-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleKey = (k) => {
    const cur = Array.isArray(f.allowed_pages) ? f.allowed_pages : [];
    setF({
      ...f,
      allowed_pages: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k],
    });
  };

  const setPreset = (keys) => setF({ ...f, allowed_pages: keys });

  const startEdit = (u) => {
    setEditing(u.id);
    setF({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      allowed_pages: u.allowed_pages || null,
    });
    setRestrict(Array.isArray(u.allowed_pages) && u.allowed_pages.length > 0);
  };

  const cancelEdit = () => {
    setEditing(null);
    setF(emptyForm);
    setRestrict(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, allowed_pages: restrict ? (f.allowed_pages || []) : null };
      if (editing) {
        // don't send blank password
        const upd = { name: payload.name, role: payload.role, allowed_pages: payload.allowed_pages };
        if (payload.password) upd.password = payload.password;
        await api.put(`/users/${editing}`, upd);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const summarize = (u) => {
    if (u.role === "admin") return "All pages (admin)";
    if (Array.isArray(u.allowed_pages) && u.allowed_pages.length > 0) {
      return u.allowed_pages.map((k) => PAGES.find((p) => p.key === k)?.label || k).join(" · ");
    }
    return "All pages (default)";
  };

  return (
    <div>
      <PageHeader
        label="Admin"
        title="Users & Access Control"
        subtitle="Create users, assign roles, and restrict which pages each user can see"
        right={
          <div className="flex gap-2">
            <button
              onClick={downloadTemplate}
              className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-white border border-border hover:bg-secondary"
              data-testid="download-template"
            >
              ↓ CSV Template
            </button>
            <label className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background cursor-pointer flex items-center hover:-translate-y-[1px] transition-transform" data-testid="bulk-import-label">
              ↑ Bulk Import CSV
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                className="hidden"
                data-testid="bulk-import-input"
              />
            </label>
          </div>
        }
      />

      {bulkResult && (
        <div className="mx-8 mt-8 border border-border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-caps">Bulk Import Result</div>
              <div className="font-display font-black text-2xl mt-1">
                {bulkResult.created} <span className="text-muted-foreground text-lg">/ {bulkResult.total} created</span>
              </div>
            </div>
            <button onClick={() => setBulkResult(null)} className="label-caps hover:text-destructive">Dismiss</button>
          </div>
          <div className="overflow-x-auto max-h-56 overflow-y-auto border border-border">
            <table className="table-dense w-full">
              <thead><tr><th>Email</th><th>Status</th><th>Detail</th></tr></thead>
              <tbody>
                {bulkResult.results.map((r, i) => (
                  <tr key={i}>
                    <td className="font-mono">{r.email}</td>
                    <td>
                      <span className={`label-caps ${r.status === "created" ? "text-green-700" : r.status === "skipped" ? "text-muted-foreground" : "text-destructive"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="text-xs text-muted-foreground">{r.detail || "OK"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card
          title={editing ? "Edit User" : "Add User"}
          right={editing && (
            <button
              type="button"
              onClick={cancelEdit}
              className="label-caps hover:text-destructive"
              data-testid="user-cancel"
            >
              Cancel
            </button>
          )}
        >
          <form onSubmit={submit} className="space-y-3" data-testid="user-form">
            <Field label="Name">
              <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" />
            </Field>
            <Field label="Email">
              <Input type="email" required disabled={!!editing} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" />
            </Field>
            <Field label={editing ? "New Password (leave blank to keep)" : "Password"}>
              <Input
                type="password"
                required={!editing}
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                data-testid="user-password"
              />
            </Field>
            <Field label="Role">
              <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role">
                <option value="admin">Admin (full access)</option>
                <option value="operator">Operator</option>
                <option value="storekeeper">Store Keeper</option>
              </Select>
            </Field>

            {f.role !== "admin" && (
              <div className="border border-border p-3 bg-secondary">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={restrict}
                    onChange={(e) => {
                      setRestrict(e.target.checked);
                      if (!e.target.checked) setF({ ...f, allowed_pages: null });
                      else if (!f.allowed_pages) setF({ ...f, allowed_pages: [] });
                    }}
                    data-testid="user-restrict-toggle"
                  />
                  <span className="label-caps">Restrict to specific pages</span>
                </label>

                {restrict && (
                  <div className="mt-3">
                    <div className="flex gap-1.5 flex-wrap mb-3">
                      <button type="button" onClick={() => setPreset(["cutting", "dispatch"])} className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 bg-white border border-border hover:bg-foreground hover:text-background">
                        Cutting + Dispatch
                      </button>
                      <button type="button" onClick={() => setPreset(["stock", "dispatch"])} className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 bg-white border border-border hover:bg-foreground hover:text-background">
                        Store Keeper
                      </button>
                      <button type="button" onClick={() => setPreset(["accessories"])} className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 bg-white border border-border hover:bg-foreground hover:text-background">
                        Accessories Only
                      </button>
                      <button type="button" onClick={() => setPreset([])} className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 bg-white border border-border hover:bg-foreground hover:text-background">
                        Clear
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                      {PAGES.map((p) => {
                        const on = (f.allowed_pages || []).includes(p.key);
                        return (
                          <label
                            key={p.key}
                            className={`flex items-center gap-2 px-2 py-1.5 border cursor-pointer text-xs ${on ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
                            data-testid={`page-${p.key}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleKey(p.key)}
                              className="accent-current"
                            />
                            {p.label}
                          </label>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-[10px] font-mono text-muted-foreground">
                      Selected: {(f.allowed_pages || []).length} page(s)
                    </div>
                  </div>
                )}
              </div>
            )}

            <Btn data-testid="user-submit">{editing ? "Save Changes" : "Create User"}</Btn>
          </form>
        </Card>

        <Card title="User List" className="lg:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Access</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="font-semibold">{u.name}</td>
                    <td className="font-mono">{u.email}</td>
                    <td><span className="label-caps">{u.role}</span></td>
                    <td className="text-xs text-muted-foreground max-w-[300px]">{summarize(u)}</td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={() => startEdit(u)}
                        className="label-caps hover:text-foreground mr-3"
                        data-testid={`user-edit-${u.email}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => del(u.id)}
                        className="label-caps hover:text-destructive"
                        data-testid={`user-del-${u.email}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={5}><Empty text="No users yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
